import React, { useState, useEffect } from 'react';
import { FileText, Download, CheckCircle, Sparkles, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import { renderSignatureToDataURL, getSignatureFontFamily } from '../lib/signatureRenderer';
import { generateAgreementWithClaude, Agreement } from '../lib/agreementService';
import { markOpportunityWon } from '../lib/crmService';

interface Brand {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  contact_info: any;
  ceo_name?: string;
  ceo_title?: string;
}

interface Estimate {
  id: string;
  brand_id: string;
  client_name: string;
  client_type: string;
  config: any;
  totals: any;
  created_at: string;
}

interface TermsAndConditions {
  id: string;
  brand_id: string;
  service_types: string[];
  terms_content: string;
  effective_date: string;
}

interface ContractViewProps {
  estimate: Estimate;
  brand: Brand;
  onBack: () => void;
  onContractSigned: () => void;
}

export function ContractView({ estimate, brand, onBack, onContractSigned }: ContractViewProps) {
  const [terms, setTerms] = useState<TermsAndConditions | null>(null);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [clientName, setClientName] = useState(estimate?.client_name || '');
  const [clientEmail, setClientEmail] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [loading, setLoading] = useState(true);
  const [generatingAgreement, setGeneratingAgreement] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreesToTerms, setAgreesToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    fetchTermsAndAgreement();
  }, [estimate?.id, brand?.id]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    setError(null);
    await fetchTermsAndAgreement();
    setLastRefresh(new Date());
    setRefreshing(false);
  };

  const fetchTermsAndAgreement = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!estimate || !estimate.config) {
        throw new Error('Invalid estimate data');
      }

      console.log('Fetching agreement for estimate:', estimate.id, 'at', new Date().toISOString());

      const { data: existingAgreement, error: agreementError } = await supabase
        .from('agreements')
        .select('*')
        .eq('estimate_id', estimate.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (agreementError) {
        console.error('Error fetching agreement:', agreementError);
      }

      if (existingAgreement) {
        console.log('Found existing agreement:', existingAgreement.id);
        setAgreement(existingAgreement);
      } else {
        console.log('No existing agreement found, checking if user is authenticated');

        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          console.log('User is authenticated, generating new agreement');
          setGeneratingAgreement(true);
          const result = await generateAgreementWithClaude({
            estimateId: estimate.id,
            brandId: brand.id,
            clientName: estimate.client_name,
            clientType: estimate.client_type,
            services: estimate.config,
            totals: estimate.totals,
            discounts: estimate.config.discountCode ? { code: estimate.config.discountCode } : undefined,
          });

          setGeneratingAgreement(false);

          if (result.success && result.agreement) {
            console.log('Agreement generated successfully:', result.agreement.id);
            setAgreement(result.agreement);
          } else {
            console.error('Agreement generation failed:', result.error);
            throw new Error(result.error || 'Failed to generate agreement');
          }
        } else {
          console.log('User not authenticated, falling back to default terms');
          const serviceTypes = [];
          if (estimate.config.includeWeb) serviceTypes.push('web');
          if (estimate.config.includeMarketing) serviceTypes.push('marketing');

          console.log('Looking for terms and conditions for brand:', brand.id);

          const { data, error } = await supabase
            .from('terms_and_conditions')
            .select('*')
            .eq('brand_id', brand.id)
            .eq('is_active', true)
            .order('effective_date', { ascending: false })
            .limit(1)
            .maybeSingle();

          console.log('Terms query result:', { data, error, brandId: brand.id });

          if (error) {
            console.error('Error fetching terms:', error);
          }

          if (data) {
            console.log('Found terms and conditions:', data.id);
            setTerms(data);
          } else {
            console.log('No terms found, using default terms');
            setTerms({
              id: 'default',
              brand_id: brand.id,
              service_types: [],
              terms_content: getDefaultTerms(),
              effective_date: new Date().toISOString()
            });
          }
        }
      }
    } catch (error) {
      console.error('Error in fetchTermsAndAgreement:', error);
      setError(error instanceof Error ? error.message : 'Failed to load contract');

      console.log('Setting default terms due to error');
      setTerms({
        id: 'default',
        brand_id: brand?.id || '',
        service_types: [],
        terms_content: getDefaultTerms(),
        effective_date: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const getDefaultTerms = () => {
    return `
      <h3>Terms and Conditions</h3>
      <p>By signing this contract, you agree to the following terms and conditions:</p>

      <h4>1. Services</h4>
      <p>The service provider agrees to deliver the services outlined in this estimate according to the specifications provided.</p>

      <h4>2. Payment Terms</h4>
      <p>Payment will be made according to the schedule outlined in this estimate. All payments are due within 30 days of invoice date.</p>

      <h4>3. Project Timeline</h4>
      <p>The project timeline will be established upon contract signing and mutual agreement. Delays caused by client feedback or revisions may extend the timeline.</p>

      <h4>4. Revisions</h4>
      <p>The number of revisions included is specified in the estimate. Additional revisions will be charged at the rates outlined.</p>

      <h4>5. Intellectual Property</h4>
      <p>Upon full payment, all intellectual property rights for the deliverables will transfer to the client.</p>

      <h4>6. Termination</h4>
      <p>Either party may terminate this agreement with written notice. Client will be responsible for payment of work completed up to the termination date.</p>

      <h4>7. Confidentiality</h4>
      <p>Both parties agree to keep confidential information private and not disclose it to third parties.</p>
    `;
  };


  const handleSignContract = async () => {
    if (!signatureText.trim() || !clientName.trim() || !clientEmail.trim() || !agreesToTerms) {
      alert('Please provide your name, email, signature, and agree to the terms to continue.');
      return;
    }

    try {
      setSubmitting(true);

      const { data, error } = await supabase
        .from('proposal_responses')
        .insert({
          estimate_id: estimate.id,
          status: 'accepted',
          signature_data: signatureText,
          signed_at: new Date().toISOString(),
          client_name: clientName,
          client_email: clientEmail
        })
        .select()
        .maybeSingle();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No response received from database');
      }

      if (agreement) {
        await supabase
          .from('agreements')
          .update({
            proposal_response_id: data.id,
            status: 'signed',
            signed_at: new Date().toISOString(),
          })
          .eq('id', agreement.id);
      }

      const { data: opportunity } = await supabase
        .from('opportunities')
        .select('id')
        .eq('estimate_id', estimate.id)
        .maybeSingle();

      if (opportunity) {
        await markOpportunityWon(opportunity.id);
      }

      await generateSignedContractPDF(data.id);

      onContractSigned();
    } catch (error) {
      console.error('Error signing contract:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`There was an error signing the contract: ${errorMessage}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const getImageDataURL = async (url: string): Promise<{ dataUrl: string; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            width: img.width,
            height: img.height
          });
        } else {
          reject(new Error('Failed to get canvas context'));
        }
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const generateSignedContractPDF = async (responseId: string) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 0;
    const rgb = hexToRgb(brand.primary_color);
    const rgb2 = hexToRgb(brand.secondary_color);

    const useWhiteBackground = brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company';

    if (!useWhiteBackground) {
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.rect(0, 0, pageWidth, 45, 'F');
    }

    if (brand.logo_url) {
      try {
        const logoData = await getImageDataURL(brand.logo_url);
        const maxWidth = 70;
        const maxHeight = useWhiteBackground ? 25 : 18;
        const aspectRatio = logoData.width / logoData.height;

        let logoWidth = maxWidth;
        let logoHeight = maxWidth / aspectRatio;

        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
          logoWidth = maxHeight * aspectRatio;
        }

        const logoX = 15;
        const logoY = useWhiteBackground ? 20 : (13 + (maxHeight - logoHeight) / 2);

        doc.addImage(logoData.dataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } catch (e) {
        console.error('Failed to load logo:', e);
        if (useWhiteBackground) {
          const darkTeal = { r: 11, g: 83, b: 148 };
          doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
        } else {
          doc.setTextColor(255, 255, 255);
        }
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text(brand.name, 15, useWhiteBackground ? 35 : 28);
      }
    }

    yPos = 58;

    const lightGray = { r: 248, g: 250, b: 252 };
    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(15, yPos, 90, 26, 2, 2, 'F');

    doc.setDrawColor(rgb.r, rgb.g, rgb.b);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, yPos, 90, 26, 2, 2, 'D');

    const darkTeal = { r: 11, g: 83, b: 148 };
    doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT INFORMATION', 20, yPos + 7);

    const textSecondary = { r: 75, g: 85, b: 99 };
    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(clientName, 20, yPos + 13);
    doc.text(new Date().toLocaleDateString(), 20, yPos + 18);
    doc.text(clientEmail, 20, yPos + 23);

    yPos += 34;

    doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('SERVICE AGREEMENT', 15, yPos);
    yPos += 2;

    doc.setDrawColor(rgb.r, rgb.g, rgb.b);
    doc.setLineWidth(1);
    doc.line(15, yPos, 180, yPos);
    yPos += 12;

    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Agreement Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 15, yPos);
    yPos += 12;

    doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('PROJECT SUMMARY', 15, yPos);
    yPos += 8;

    const summaryBoxHeight = 17;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.roundedRect(15, yPos, 180, summaryBoxHeight, 2, 2, 'FD');

    yPos += 7;
    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    const services = [];
    if (estimate.config.includeWeb) services.push('Web Development');
    if (estimate.config.includeMarketing) services.push('Digital Marketing');
    doc.text('Services:', 20, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(services.join(', '), 180, yPos, { align: 'right' });

    yPos += 16;

    if (estimate.totals?.discountAmount && estimate.totals.discountAmount > 0) {
      doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('PRICING BREAKDOWN', 15, yPos);
      yPos += 8;

      const pricingBoxHeight = 24;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.3);
      doc.roundedRect(15, yPos, 180, pricingBoxHeight, 2, 2, 'FD');

      yPos += 7;
      doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');

      const originalTotal = (estimate.totals?.grandTotal || 0) + (estimate.totals.discountAmount || 0);
      doc.text('Original Price:', 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${originalTotal.toLocaleString()}`, 180, yPos, { align: 'right' });

      yPos += 6;
      doc.setFont('helvetica', 'normal');
      const discountText = estimate.config?.discountCode ? `Discount (${estimate.config.discountCode}):` : 'Discount Applied:';
      doc.text(discountText, 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 197, 94);
      doc.text(`-$${(estimate.totals.discountAmount || 0).toLocaleString()}`, 180, yPos, { align: 'right' });

      yPos += 6;
      doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
      doc.setFont('helvetica', 'normal');
      doc.text('Setup Fees:', 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${(estimate.totals?.setupFees || 0).toLocaleString()}`, 180, yPos, { align: 'right' });

      yPos += 20;
    }

    const accentGreen = { r: 212, g: 245, b: 110 };
    const investmentBoxHeight = 16;
    doc.setFillColor(accentGreen.r, accentGreen.g, accentGreen.b);
    doc.roundedRect(15, yPos, 180, investmentBoxHeight, 2, 2, 'F');

    yPos += 11;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
    doc.text('TOTAL INVESTMENT:', 20, yPos);
    doc.setFontSize(16);
    doc.text(`$${(estimate.totals?.grandTotal || estimate.totals?.total_due || 0).toLocaleString()}`, 180, yPos, { align: 'right' });

    yPos += 18;

    if (terms && terms.terms_content) {
      doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('TERMS AND CONDITIONS', 15, yPos);
      yPos += 8;

      const stripHtmlTags = (html: string): string => {
        return html
          .replace(/<h[1-6][^>]*>/gi, '')
          .replace(/<\/h[1-6]>/gi, '\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<p[^>]*>/gi, '')
          .replace(/<\/p>/gi, '\n')
          .replace(/<li[^>]*>/gi, '• ')
          .replace(/<\/li>/gi, '\n')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/\n\s*\n\s*\n/g, '\n\n')
          .trim();
      };

      const cleanTerms = stripHtmlTags(terms.terms_content);

      doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
      doc.setFontSize(9);
      doc.setFont('times', 'normal');

      const termsLines = doc.splitTextToSize(cleanTerms, 170);

      for (let i = 0; i < termsLines.length; i++) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(termsLines[i], 20, yPos);
        yPos += 4.5;
      }

      yPos += 8;
    }

    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }

    doc.setTextColor(darkTeal.r, darkTeal.g, darkTeal.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURES', 15, yPos);
    yPos += 8;

    const signatureBoxHeight = 35;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.roundedRect(15, yPos, 180, signatureBoxHeight, 2, 2, 'FD');

    const signatureY = yPos + 10;
    const leftX = 25;
    const rightX = 105;

    try {
      const clientSigImage = await renderSignatureToDataURL({
        text: signatureText,
        color: brand.primary_color,
        fontSize: 32,
        width: 300,
        height: 60
      });

      doc.addImage(clientSigImage, 'PNG', leftX, signatureY - 8, 60, 15);
    } catch (error) {
      console.error('Failed to render client signature:', error);
      doc.setFontSize(22);
      doc.setFont('Times', 'Italic');
      if (rgb) {
        doc.setTextColor(rgb.r, rgb.g, rgb.b);
      }
      doc.text(signatureText, leftX, signatureY);
    }

    if (rgb) {
      doc.setDrawColor(rgb.r, rgb.g, rgb.b);
      doc.setLineWidth(0.8);
    }
    doc.line(leftX, signatureY + 2, leftX + 75, signatureY + 2);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    doc.text(clientName, leftX, signatureY + 9);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    doc.text('Client Signature', leftX, signatureY + 14);

    const ceoName = brand.ceo_name || 'Leonardo Barros';

    try {
      const ceoSigImage = await renderSignatureToDataURL({
        text: ceoName,
        color: brand.primary_color,
        fontSize: 32,
        width: 300,
        height: 60
      });

      doc.addImage(ceoSigImage, 'PNG', rightX, signatureY - 8, 60, 15);
    } catch (error) {
      console.error('Failed to render CEO signature:', error);
      doc.setFontSize(22);
      doc.setFont('Times', 'Italic');
      if (rgb) {
        doc.setTextColor(rgb.r, rgb.g, rgb.b);
      }
      doc.text(ceoName, rightX, signatureY);
    }

    if (rgb) {
      doc.setDrawColor(rgb.r, rgb.g, rgb.b);
      doc.setLineWidth(0.8);
    }
    doc.line(rightX, signatureY + 2, rightX + 75, signatureY + 2);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    doc.text(ceoName, rightX, signatureY + 9);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(textSecondary.r, textSecondary.g, textSecondary.b);
    const ceoTitle = brand.ceo_title || 'CEO';
    doc.text(`${ceoTitle}, ${brand.name}`, rightX, signatureY + 14);

    doc.save(`signed-contract-${responseId}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Error Loading Contract</p>
            <p className="text-sm">{error}</p>
          </div>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!estimate || !brand) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">Missing required data</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const brandGradient = `linear-gradient(135deg, ${brand.primary_color} 0%, ${brand.secondary_color} 100%)`;
  const brandGradientSubtle = `linear-gradient(135deg, ${brand.primary_color}15 0%, ${brand.secondary_color}15 100%)`;
  const useWhiteBackground = brand.name === 'Web Design Miami Company' || brand.name === 'West Palm Beach Web Design Company';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div
            className="p-8"
            style={{
              background: useWhiteBackground ? 'white' : brandGradient,
              color: useWhiteBackground ? '#000000' : '#ffffff'
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                {brand.logo_url && (
                  <img
                    src={brand.logo_url}
                    alt={brand.name}
                    className="h-12 sm:h-16 object-contain mb-4"
                  />
                )}
                <div className="flex items-center space-x-3">
                  <FileText className="w-8 h-8" style={{ color: useWhiteBackground ? '#000000' : '#ffffff' }} />
                  <h1 className="text-2xl sm:text-3xl font-bold">Service Agreement</h1>
                </div>
                <p className="mt-2" style={{ color: useWhiteBackground ? '#000000' : 'rgba(255, 255, 255, 0.9)' }}>
                  Please review and sign below to accept this proposal
                </p>
              </div>
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: useWhiteBackground ? brand.primary_color : 'rgba(255, 255, 255, 0.2)',
                  color: useWhiteBackground ? '#ffffff' : '#ffffff'
                }}
                title="Refresh agreement and terms"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {lastRefresh && (
              <div className="text-xs text-gray-500 text-right mb-2">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
            )}
            <div className="rounded-lg p-6" style={{ background: brandGradientSubtle }}>
              <h2 className="font-semibold mb-3 text-lg" style={{ color: brand.primary_color }}>Estimate Summary</h2>
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-medium">Client:</span> {estimate.client_name}</p>
                <p><span className="font-medium">Total Investment:</span> ${(estimate.totals?.grandTotal || estimate.totals?.total_due || 0).toLocaleString()}</p>
                <p><span className="font-medium">Created:</span> {new Date(estimate.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold" style={{ color: brand.primary_color }}>
                  {agreement ? 'Service Agreement' : 'Terms and Conditions'}
                </h2>
              </div>

              {generatingAgreement && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="font-medium text-blue-900">Creating Your Custom Agreement</p>
                      <p className="text-sm text-blue-700">Claude AI is analyzing your services and generating a tailored agreement...</p>
                    </div>
                  </div>
                </div>
              )}

              {agreement && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <div className="text-sm text-blue-900">
                    <p className="font-medium">Custom Agreement Prepared</p>
                    <p className="text-blue-700 mt-1">
                      This agreement has been specifically prepared for your project based on the selected services.
                      Please review the terms below carefully before accepting.
                    </p>
                  </div>
                </div>
              )}

              {agreement ? (
                <div
                  className="prose prose-sm max-w-none bg-gray-50 p-6 rounded-lg max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: agreement.agreement_html || agreement.agreement_content }}
                />
              ) : terms ? (
                <div
                  className="prose prose-sm max-w-none bg-gray-50 p-6 rounded-lg max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: terms.terms_content }}
                />
              ) : (
                <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
                  <p>No agreement or terms available. Please contact support.</p>
                </div>
              )}
            </div>

            <div className="border-t pt-6 space-y-4">
              <h2 className="text-xl font-semibold mb-6" style={{ color: brand.primary_color }}>Client Information</h2>
              <div className="p-6 rounded-lg" style={{ background: brandGradientSubtle }}>

              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                  style={{
                    '--tw-ring-color': brand.primary_color
                  } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = brand.primary_color}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                  style={{
                    '--tw-ring-color': brand.primary_color
                  } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = brand.primary_color}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Electronic Signature *
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Type your full legal name below. This will serve as your electronic signature.
                </p>
                <input
                  type="text"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent italic text-xl transition-all"
                  style={{
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                    '--tw-ring-color': brand.primary_color
                  } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = brand.primary_color}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Type your full name"
                />
                {signatureText && (
                  <div className="mt-3 p-4 bg-white border-2 rounded-lg" style={{ borderColor: brand.primary_color + '40' }}>
                    <p className="text-xs font-medium mb-2" style={{ color: brand.primary_color }}>Signature Preview:</p>
                    <div className="pb-2" style={{ borderBottom: `2px solid ${brand.primary_color}` }}>
                      <p className="text-2xl italic" style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive", color: brand.primary_color }}>
                        {signatureText}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreesToTerms}
                  onChange={(e) => setAgreesToTerms(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 focus:ring-2"
                  style={{
                    accentColor: brand.primary_color,
                    '--tw-ring-color': brand.primary_color
                  } as React.CSSProperties}
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the terms and conditions outlined above, and I authorize the services described in this estimate.
                </span>
              </label>
              </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-6 border-t">
              <button
                onClick={onBack}
                className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
              >
                Back to Estimate
              </button>
              <button
                onClick={handleSignContract}
                disabled={submitting || !signatureText.trim() || !clientName.trim() || !clientEmail.trim() || !agreesToTerms}
                className="px-8 py-4 text-white font-bold text-lg rounded-xl disabled:bg-gray-300 disabled:cursor-not-allowed transition-all transform hover:scale-105 hover:shadow-xl flex items-center justify-center space-x-2"
                style={{
                  background: submitting || !signatureText.trim() || !clientName.trim() || !clientEmail.trim() || !agreesToTerms ? undefined : brandGradient
                }}
              >
                <CheckCircle className="w-6 h-6" />
                <span>{submitting ? 'Processing...' : 'Sign & Accept Contract'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
